<template>
  <div class="sm:mt-0 mt-14">
    <ArtistSkeleton v-if="loading" />
    <div v-else>
      <div v-if="artist">
        <ProfileInfoSticky v-if="y > 200" :user="artist" />
        <div class="md:relative md:bg-gray-200 w-full md:h-48">
          <div
            class="md:absolute md:w-[90%] w-full sm:rounded-lg md:left-1/2 md:transform md:-translate-x-1/2 md:top-14 border border-gray-100 bg-white overflow-hidden sm:mb-0 mb-5"
          >
            <div
              class="flex flex-col sm:flex-row sm:justify-between p-3.5 md:p-8 gap-8"
            >
              <div class="flex lg:gap-x-5 md:gap-x-3.5 gap-x-2.5">
                <div class="">
                  <BaseImage
                    @contextmenu.prevent
                    @dragstart.prevent
                    v-if="artist.avatar"
                    :uri="artist.avatar"
                    img-class="rounded-full sm:size-40 size-30 sm:min-w-40 min-w-30 object-cover border border-gray-100"
                    :alt="
                      `${artist.name || ''} ${artist.last_name || ''}` ||
                      'Medium.am user'
                    "
                  />
                  <BaseImage
                    v-else
                    @contextmenu.prevent
                    @dragstart.prevent
                    uri="/no_avatar.jpg"
                    img-class="rounded-full sm:size-40 size-30 sm:min-w-40 min-w-30 object-cover border border-gray-100"
                    alt="Placeholder avatar"
                  />
                </div>
                <div>
                  <h1
                    class="lg:text-4xl md:text-2xl text-lg font-bold text-black"
                  >
                    {{ artist.name }} {{ artist.last_name }}
                  </h1>
                  <p
                    v-if="artist?.original_name"
                    class="italic sm:text-sm text-xs text-gray-500 mt-1"
                  >
                    {{ artist.original_name }}
                  </p>
                  <div
                    v-if="artist.professions.length"
                    class="mt-1 flex flex-wrap sm:gap-2 gap-1 items-center sm:justify-start justify-center"
                  >
                    <span
                      class="flex items-center justify-center px-2 py-0.5 rounded text-primary-blue bg-primary-blue/5 text-xs"
                      v-for="(p, i) in artist.professions"
                      :key="i"
                      >{{ p.label[locale as Locale] }}</span
                    >
                  </div>
                  <span
                    v-if="
                      artist?.createdAt &&
                      !artist.email?.endsWith('@arm-art.com')
                    "
                    class="px-3 py-1 bg-gray-100 rounded text-[11px]"
                  >
                    {{ $t("artist_details.joined") }}
                    {{ new Date(artist.createdAt).getFullYear() }}
                  </span>
                </div>
              </div>

              <div class="flex flex-col">
                <div
                  class="flex flex-col sm:flex-row justify-between items-start sm:gap-x-5"
                >
                  <div class="flex flex-col space-y-2.5 sm:w-auto w-full">
                    <div class="md:min-w-52 space-y-2.5">
                      <div class="w-full flex items-center justify-between">
                        <p class="text-sm">
                          {{ $t("artist_details.artworks") }}
                        </p>
                        <div
                          class="size-7 min-w-7 rounded-full flex items-center justify-center text-[11px] bg-gray-200"
                        >
                          {{ artist?.artworks_count || 0 }}
                        </div>
                      </div>

                      <div class="w-full flex items-center justify-between">
                        <p class="text-sm">
                          {{ $t("artist_details.followers") }}
                        </p>
                        <div
                          class="size-7 min-w-7 rounded-full flex items-center justify-center text-[11px] bg-gray-200"
                        >
                          {{ artist.followersCount || 0 }}
                        </div>
                      </div>
                    </div>
                    <ArtistFollowButton
                      :following-id="artist._id"
                      :following-email="artist.email"
                      :loading="loading"
                    />
                    <ClientOnly>
                      <ProfileLink
                        btn-class="flex items-center justify-center space-x-2 text-[15px] w-full h-11 rounded-lg"
                        wrapper-class="pt-0"
                        rouunded="rounded-none"
                        :profile-link="`https://medium.am/artist/${artist?._id}`"
                      />
                    </ClientOnly>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Artist biography  -->

        <div
          class="border border-gray-100 sm:rounded-lg bg-white overflow-hidden sm:mb-0 mb-5 p-3.5 md:p-8 md:mt-30 mt-2.5"
        >
          <h2 class="mb-2.5 font-medium xl:text-xl md:text-lg text-base">
            {{ $t("artist_details.about_artist") }}
          </h2>
          <BaseDetails
            :content="getBioContent(artist!)"
            :title="''"
            :threshold="400"
            :empty-text="$t('artist_details.no_bio')"
            content-class="sm:text-[15px] text-sm"
          />
        </div>
      </div>

      <div v-else class="py-20">
        <BaseNotFound
          @click="backToGallery"
          :action="true"
          :label="$t('not_found.artwork.label')"
          :title="$t('not_found.artist.title')"
        >
          <template #icon>
            <Icon name="tabler:user-filled" size="30" />
          </template>
        </BaseNotFound>
      </div>

      <div v-if="artist">
        <h2 class="text-2xl font-bold px-2 my-6">
          <template v-if="locale === 'hy'">
            {{ $t("artist_details.collection_by") }}
          </template>
          <template v-else>
            {{ $t("artist_details.collection_by") }} {{ artist?.name }}
            {{ artist?.last_name }}
          </template>
        </h2>
        <slot name="artist-gallery"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import type { Locale } from "~~/types/global";
import type { AppArtist } from "~~/types/artist";
import { useWindowScroll } from "@vueuse/core";

const { y } = useWindowScroll();
const route = useRoute();
const localePath = useLocalePath();
const { locale } = useI18n();

const artistId = computed<string>(() => route.params.id as string);

const { data, pending: loading } = useApi<any>(`/artists/${artistId.value}`);

const artist = computed<AppArtist | null>(() => {
  // Check if data exists AND if the nested .data property exists
  return data.value?.data ?? null;
});

const fullName = computed(() => {
  const parts = [artist.value?.name, artist.value?.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "Artist";
});

const getBioContent = (artist: AppArtist): string => {
  if (!artist) return "";
  const bio = artist.biography[locale.value] || artist.bio || "";
  return bio;
};

const backToGallery = () => navigateTo(localePath("/gallery"));

useSeoMeta({
  title: () => `${fullName.value} - MEDIUM.am`,
  ogTitle: () => `${fullName.value} - MEDIUM.am`,
  description: () =>
    artist.value?.bio ||
    `Explore the portfolio of ${fullName.value}. Discover ${artist.value?.artworks_count || 0} unique artworks on MEDIUM.am.`,
  ogDescription: () =>
    artist.value?.bio || `Explore the portfolio of ${fullName.value}.`,
  ogImage: () => artist.value?.avatar || "https://medium.am/logo.png",
  twitterImage: () => artist.value?.avatar || "https://medium.am/logo.png",
  ogType: "profile",
  twitterCard: "summary",
  ogUrl: () => `https://medium.am/artists/${artist.value?._id}`,
  profileFirstName: () => artist.value?.name || "",
  profileLastName: () => artist.value?.last_name || "",
  profileUsername: () => artist.value?.nickname || artist.value?._id || "",
});
</script>
